import { AIMessage, UserMessage } from "@/features/Prompt/schema";
import { ExportedMessageRepository, ThreadMessage } from "@assistant-ui/react";
import {
  IdUtils,
  MessageTree,
  MessageTreeUtils,
  MessageUtils,
  MessageWithParent,
} from "./utils";

/**
 * Type for mapping message IDs to their thread message IDs
 */
type ProcessedIdMap = Record<number, string>;

/**
 * Handles conversion of AI messages to thread export repository format
 */
export class MessageConverter {
  /**
   * Determines if a message is a user message
   * @param msg - The message to check
   */
  static isUserMessage(msg: AIMessage): boolean {
    return msg.role === "user";
  }

  /**
   * Extracts parent ID from a message
   * @param msg - The message to extract parent ID from
   */
  static _extractParentId(msg: AIMessage): number | null {
    const message = msg as { parent_id?: number | null; id: number };
    return message.parent_id ?? null;
  }

  /**
   * Creates the exported message repository from processed messages
   * @param result - The array of processed messages
   */
  static _createExportedRepository(
    result: MessageWithParent<ThreadMessage>[],
  ): ExportedMessageRepository {
    return {
      headId: result.length > 0 ? result[result.length - 1].message.id : null,
      messages: result,
    };
  }

  /**
   * Processes a user message
   * @param msg - The user message to process
   * @param id - The ID for the new message
   * @param rawParentId - The parent message ID
   */
  static _processUserMessage(
    msg: AIMessage,
    id: string,
    rawParentId: number | null,
  ): MessageWithParent<ThreadMessage> {
    return MessageUtils.convertUserMessage({
      msg: msg as UserMessage,
      id,
      rawParentId,
    });
  }

  /**
   * Processes an unknown message type
   * @param msg - The message to process
   * @param id - The ID for the new message
   * @param rawParentId - The parent message ID
   */
  static _processUnknownMessage(
    msg: AIMessage,
    id: string,
    rawParentId: number | null,
  ): MessageWithParent<ThreadMessage> {
    return MessageUtils.createDefaultMessage({
      id,
      msgId: msg.id,
      rawParentId,
      createdAt: msg.created_at,
    });
  }

  /**
   * Processes assistant and tool messages from a group of IDs
   * @param messageIds - Array of message IDs in the group
   * @param tree - The message tree
   * @param rawParentId - Parent ID for the group
   */
  static _processAssistantGroupFromTree(
    messageIds: number[],
    tree: MessageTree,
    rawParentId: number | null,
  ): MessageWithParent<ThreadMessage> {
    const messages = messageIds.map((id) => tree[id].msg);
    const lastId = messageIds[messageIds.length - 1];

    return MessageUtils.convertGroupedMessages(
      messages,
      IdUtils.createMessageId(lastId),
      rawParentId,
    );
  }

  /**
   * Process a message and its children recursively using the tree structure
   * @param msgId - ID of the message to process
   * @param tree - The message tree
   * @param result - Array to store processed messages
   * @param processedIds - Map of processed IDs to their thread message IDs
   * @param forcedParentId - Optional forced parent ID to use instead of the regular parent
   */
  static _processMessageAndChildrenRecursively(
    msgId: number,
    tree: MessageTree,
    result: MessageWithParent<ThreadMessage>[],
    processedIds: ProcessedIdMap,
    forcedParentId: string | null = null,
  ): string {
    const entry = tree[msgId];
    if (!entry || entry.processed) {
      return processedIds[msgId] || "";
    }

    const msg = entry.msg;
    let parentId = forcedParentId;
    const rawParentId = this._extractParentId(msg);

    // If no forced parent, use the regular parent if it exists and is processed
    if (
      parentId === null &&
      rawParentId !== null &&
      processedIds[rawParentId]
    ) {
      parentId = processedIds[rawParentId];
    }

    let threadMessageId = "";
    let processedMessageIds: number[] = [];

    if (this.isUserMessage(msg)) {
      // Process a user message
      const id = IdUtils.createMessageId(msg.id);
      const processedMessage = this._processUserMessage(msg, id, rawParentId);
      result.push(processedMessage);

      threadMessageId = id;
      processedMessageIds = [msg.id];
    } else if (MessageUtils.isAssistantOrToolMessage(msg)) {
      // Check if this is the start of a group of assistant/tool messages
      const groupIds = MessageTreeUtils.findConsecutiveGroupInTree(msgId, tree);
      const processedMessage = this._processAssistantGroupFromTree(
        groupIds,
        tree,
        rawParentId,
      );
      result.push(processedMessage);

      threadMessageId = processedMessage.message.id;
      processedMessageIds = groupIds;
    } else {
      // Process any other type of message
      const id = IdUtils.createMessageId(msg.id);
      const processedMessage = this._processUnknownMessage(
        msg,
        id,
        rawParentId,
      );
      result.push(processedMessage);

      threadMessageId = id;
      processedMessageIds = [msg.id];
    }

    // Mark all processed messages
    processedMessageIds.forEach((id) => {
      if (tree[id]) {
        tree[id].processed = true;
        processedIds[id] = threadMessageId;
      }
    });

    // Get the last processed message ID for child processing
    const lastProcessedId = processedMessageIds[processedMessageIds.length - 1];

    // Process all children of the last message in the group
    if (tree[lastProcessedId]) {
      tree[lastProcessedId].children.forEach((childId) => {
        this._processMessageAndChildrenRecursively(
          childId,
          tree,
          result,
          processedIds,
          threadMessageId,
        );
      });
    }

    return threadMessageId;
  }

  /**
   * Sorts messages by creation date
   * @param result - The array of messages to sort
   */
  static _sortMessagesByDate(
    result: MessageWithParent<ThreadMessage>[],
  ): MessageWithParent<ThreadMessage>[] {
    return [...result].sort((a, b) => {
      const dateA = a.message.createdAt.getTime();
      const dateB = b.message.createdAt.getTime();
      return dateA - dateB;
    });
  }

  /**
   * Converts an array of AI messages to a thread export message repository
   * @param messages - The array of AI messages to convert
   */
  static convertToThreadExportMessageRepository(
    messages: ReadonlyArray<AIMessage>,
  ): ExportedMessageRepository {
    const result: MessageWithParent<ThreadMessage>[] = [];

    const tree = MessageTreeUtils.buildMessageTree(messages);

    const rootIds = MessageTreeUtils.findRootMessageIds(messages, tree);

    const processedIds: ProcessedIdMap = {};
    rootIds.forEach((rootId) => {
      this._processMessageAndChildrenRecursively(
        rootId,
        tree,
        result,
        processedIds,
      );
    });

    const sortedResult = this._sortMessagesByDate(result);

    return this._createExportedRepository(sortedResult);
  }
}
